import { useState } from "react";
import { Star, StarOff } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface Player {
  id: string;
  name: string;
  team: string;
  position: string;
  photo_url?: string;
  is_active: boolean;
}

interface PlayerCardProps {
  player: Player;
  isCaptain?: boolean;
  onCaptainToggle?: () => void;
  showCaptainControl?: boolean;
  size?: "small" | "medium" | "large";
  lastMatchPoints?: number;
  nextOpponent?: string;
  status?: "injured" | "suspended" | "playing" | "finished";
}

export function PlayerCard({
  player,
  isCaptain = false,
  onCaptainToggle,
  showCaptainControl = false,
  size = "medium",
  lastMatchPoints,
  nextOpponent,
  status,
}: PlayerCardProps) {
  const [imageError, setImageError] = useState(false);

  const sizeClasses = {
    small: "aspect-[3/4] text-xs",
    medium: "aspect-[3/4] text-sm",
    large: "aspect-[4/5] text-base",
  };

  const getStatusBadge = () => {
    if (!status) return null;

    const statusConfig = {
      injured: { label: "Injured", variant: "destructive" as const, color: "bg-red-500" },
      suspended: { label: "Suspended", variant: "destructive" as const, color: "bg-red-500" },
      playing: { label: "Live", variant: "default" as const, color: "bg-green-500" },
      finished: { label: "Finished", variant: "secondary" as const, color: "bg-gray-500" },
    };

    const config = statusConfig[status];
    
    return (
      <div className="absolute top-1 right-1 flex items-center gap-1">
        {status === "playing" && (
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
        )}
        <Badge variant={config.variant} className="text-xs px-1 py-0">
          {config.label}
        </Badge>
      </div>
    );
  };

  const getPositionColor = (position: string) => {
    const colors = {
      GK: "bg-yellow-500",
      DEF: "bg-blue-500", 
      MID: "bg-green-500",
      FWD: "bg-red-500",
    };
    return colors[position as keyof typeof colors] || "bg-gray-500";
  };

  return (
    <Card className={cn(
      "relative overflow-hidden transition-all duration-200 hover:scale-105 cursor-pointer group",
      sizeClasses[size],
      isCaptain && "ring-2 ring-yellow-400 shadow-lg"
    )}>
      <CardContent className="p-2 h-full flex flex-col">
        {/* Status badge */}
        {getStatusBadge()}

        {/* Captain control */}
        {showCaptainControl && (
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "absolute top-1 left-1 w-6 h-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity",
              isCaptain && "opacity-100"
            )}
            onClick={(e) => {
              e.stopPropagation();
              onCaptainToggle?.();
            }}
          >
            {isCaptain ? (
              <Star className="h-3 w-3 text-yellow-400 fill-yellow-400" />
            ) : (
              <StarOff className="h-3 w-3" />
            )}
          </Button>
        )}

        {/* Player photo */}
        <div className="flex-1 flex items-center justify-center bg-muted rounded-md mb-2 relative overflow-hidden">
          {player.photo_url && !imageError ? (
            <img
              src={player.photo_url}
              alt={player.name}
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <div className="text-2xl font-bold text-muted-foreground">
                {player.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
              </div>
            </div>
          )}
        </div>

        {/* Player info */}
        <div className="space-y-1">
          <div className="font-medium truncate text-foreground">
            {player.name}
          </div>
          
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground truncate">
              {player.team}
            </span>
            <Badge 
              variant="outline" 
              className={cn("text-xs px-1 text-white border-none", getPositionColor(player.position))}
            >
              {player.position}
            </Badge>
          </div>

          {/* Match info */}
          {(lastMatchPoints !== undefined || nextOpponent) && (
            <div className="text-xs space-y-1">
              {lastMatchPoints !== undefined && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Last GW:</span>
                  <span className="font-mono font-medium">{lastMatchPoints} pts</span>
                </div>
              )}
              {nextOpponent && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">vs</span>
                  <span className="truncate font-medium">{nextOpponent}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}